import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { ETF, Fund } from './entities';
import { ETFRestfulEvent, ETFRequest } from './request';
import { ETFStore } from './store';

export type ETFManagerEvent = ETFRestfulEvent;

export class ETFManager extends Subscribable<ETFManagerEvent> {
  private request: ETFRequest;
  private store: ETFStore;

  constructor(session: Session) {
    super();
    this.request = new ETFRequest(session);
    this.store = new ETFStore();
  }

  public static getName = () => 'benzinga-etfs';

  public getETFs = async (force?: boolean): SafePromise<ETF[]> => {
    const stored = this.store.getETFs();

    if (!stored || force) {
      const response = await this.request.getETFs();
      if (response.err) {
        return response;
      } else {
        if (this.store.updateETFs(response.ok.data.etfs)) {
          this.dispatch({
            etfs: response.ok.data.etfs,
            type: 'get_etfs',
          });
        }
      }
      return { ok: response.ok.data.etfs };
    }

    return { ok: stored ?? [] };
  };

  public getETF = async (id: number): SafePromise<ETF> => {
    const stored = this.store.getETF(id);

    if (!stored) {
      const response = await this.request.getETF(id);
      if (response.err) {
        return response;
      } else {
        if (this.store.updateETF(response.ok.data)) {
          this.dispatch({
            etf: response.ok.data,
            type: 'get_etf',
          });
        }
      }
      return { ok: response.ok.data };
    }

    return { ok: stored };
  };

  public getFunds = async (force?: boolean): SafePromise<Fund[]> => {
    const stored = this.store.getFunds();

    if (!stored || force) {
      const response = await this.request.getFunds();
      if (response.err) {
        return response;
      } else {
        if (this.store.updateFunds(response.ok?.data?.funds ?? [])) {
          this.dispatch({
            funds: response.ok?.data?.funds ?? [],
            type: 'get_funds',
          });
        }
      }
      return { ok: response.ok?.data?.funds ?? [] };
    }

    return { ok: stored ?? [] };
  };

  public getFund = async (id: number | string): SafePromise<Fund> => {
    const stored = this.store.getFund(id);

    if (!stored) {
      const response = await this.request.getFund(id);
      if (response.err) {
        return response;
      } else {
        if (response?.ok?.data && this.store.updateFund(response.ok.data)) {
          this.dispatch({
            fund: response.ok.data,
            type: 'get_fund',
          });
        }
      }
      return { ok: response.ok.data };
    }

    return { ok: stored };
  };
}
